import "./OrderDepthComponentV2.scss";
import templateDesktop from "./OrderDepthComponentV2.html";
import {IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {OrderDepthModels} from "../../models/OrderDepthModels";
import {QuoteViewModel} from "../../../quote/models/QuoteViewModel";
import {OrderDepthStore} from "../../store/OrderDepthStore";
import {QuoteStore} from "../../../quote/store/QuoteStore";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {buildQuoteRequest} from "../../../quote/util/QuoteStoreUtil";
import {CompanyInfo} from "../../../types/Company";
import {ComponentCallback} from "../../../types/AngularJS";
import {CodeAndExchange} from "../../../instruments/types/CodeAndExchange";
import {DepthService} from "../../../services/depth/DepthService";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";
import {ThemingStore} from "../../../theming/store/ThemingStore";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {CurrentUserService} from "../../../services/user/CurrentUserService";

export const OrderDepthV2 = "tfOrderDepthV2";

export function orderDepthV2() {
    return {
        template: templateDesktop,
        bindings: {
            security: "<",
            stockDisabled: "<",
            initialRowsDisplayed: "<",
            onSecurityChanged: "&"
        },
        controllerAs: "ctrl",
        controller: OrderDepthControllerV2
    };
}

export class OrderDepthControllerV2 implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.orderDepthStore.updateCode(security);
            this.strategy = this.priceStrategyService.forCode(security.code);
        }
    }

    /** @input */
    public set initialRowsDisplayed(rows: number | null) {
        if (rows != null) {
            this.orderDepthStore.updateDefaultRowCount(rows);
        }
    }

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    public readonly currentCode$: Observable<CodeAndExchange>;
    public readonly currentExchange$ = new ReplaySubject<string>(1);
    public readonly hasHiddenRows$: Observable<boolean>;
    public stockDisabled = false;
    public readonly bids$: Observable<OrderDepthModels>;
    public readonly asks$: Observable<OrderDepthModels>;
    public currentExchange: string | null;
    public tableIsHidden = false;
    public high?: number | null;
    public low?: number | null;
    public last?: number | null;
    public readonly currentQuote$ = new ReplaySubject<QuoteViewModel>(1);
    public strategy: PriceStrategy;
    private readonly isLivePrices: boolean;
    private readonly orderDepthStore: OrderDepthStore;
    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    private depthService: DepthService;
    /** @ngInject */
    public constructor(private readonly quoteStore: QuoteStore,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly themingStore: ThemingStore,
                       depthService: DepthService,
                       systemEventService: SystemEventService,
                       private readonly currentUserService: CurrentUserService) {

        this.depthService = depthService;
        this.orderDepthStore = new OrderDepthStore(this.depthService, systemEventService, currentUserService);
        this.currentCode$ = this.orderDepthStore.currentCode;
        this.bids$ = this.orderDepthStore.bids;
        this.asks$ = this.orderDepthStore.asks;
        this.isLivePrices = currentUserService.user.isLivePrices();
        this.hasHiddenRows$ = this.orderDepthStore
                                  .depth
                                  .map((d) => d.hasHiddenRows)
                                  .publishReplay(1)
                                  .refCount();
        this.themingStore
            .currentTheme
            .takeUntil(this.destroyed$)
            .subscribe((theme) => this.orderDepthStore.updateTheme(theme));
    }

    public $onInit(): void {
        this.watchCodeQuote();
    }

    private watchCodeQuote() {
        this.currentCode$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((security) => buildQuoteRequest(security, PortfolioType.BROKER))
            .switchMap((quoteRequest) => this.quoteStore.stream(quoteRequest))
            .subscribe((q) => {
                this.high = q.high;
                this.low = q.low;
                this.last = q.last;
                this.currentQuote$.next(q);
                this.currentExchange$.next(q.exchange);
            });
    }

    private watchCurrentExchange() {
        this.currentExchange$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .subscribe( v => this.currentExchange = v );
    }

    public async selectSecurity(company?: CompanyInfo) {
        if (company != null) {
            const security = ObjectUtil.pick(company, "code", "exchange", "name");
            if (this.isLivePrices) {this.orderDepthStore.updateCode(security); }
            this.onSecurityChanged({company});
            this.currentExchange$.next(company.exchange);
        }
    }

    public toggleBidExpansion(price: number) {
        this.orderDepthStore.toggleBid(price);
    }

    public toggleAskExpansion(price: number) {
        this.orderDepthStore.toggleAsk(price);
    }

    public toggleTable() {
        this.tableIsHidden = !this.tableIsHidden;
    }

    public showHiddenRows() {
        this.orderDepthStore.showHiddenRows();
    }

    public $onDestroy(): void {
        this.orderDepthStore.destroy();

        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
